/* eslint-disable max-len*/

import BuyItNowAPICLIENT from "../../modules/BuyItNowAPIClient";
import ContactFormFields from "./ContactForm";
import LoadingSpinner from "../LoadingSpinner";
import PropTypes from "prop-types";
import React from "react";

export default class ContactInformationForm extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      "attemptedExistingEmail": false,
      "city": "",
      "country": "United States",
      "emailAddress": "",
      "firstName": "",
      "lastName": "",
      "loading": false,
      "phoneNumber": "",
      "state": "",
      "streetAddress": "",
      "unitNumber": "",
      "zipCode": ""
    };
  }

  // this is a callback wrapped in mapVerifiedAddressToState since we need to do it after the state update address verf
  createContact = () => {
    const contactInfo = this.state;
    BuyItNowAPICLIENT.createContact(contactInfo, this.handleContactServerResponse, this.handleContactServerError);
  }

  // clear the contact in parent comp if someone makes edits to an existing one, but it fails to be saved
  clearContact = () => {
    const {clearContact} = this.props;
    clearContact();
  }

  handleContactServerResponse = (response) => {
    const {handleSuccessfulContactCreation, handleDisplayErrors} = this.props;

    this.setState({"loading": false});

    if (response.success) {
      handleSuccessfulContactCreation(response.contact);
    } else if (response.existing_customer_prompt) {
      handleDisplayErrors({
        "component": "contactInformationForm",
        "errorMessage": "Existing email"
      });
      // this gets appended to params and allows contacts to proceed with used customer email after being stopped once
      this.setState({"attemptedExistingEmail": true});
    } else {
      handleDisplayErrors({
        "component": "contactInformationForm",
        "errorMessage": response.error
      });
    }
  }

  handleContactServerError = () => {
    const {handleDisplayErrors} = this.props;
    this.setState({"loading": false});
    handleDisplayErrors({
      "component": "contactInformationForm",
      "errorMessage": "Oops! Something went wrong when saving your information. Please make sure everything looks correct and try again."
    });
    this.clearContact();
  }

  handleInputValidations = () => {
    const {handleDisplayErrors} = this.props;
    const missingFields = this.emptyInputsCheck();
    if (missingFields) {
      handleDisplayErrors({
        "component": "contactInformationForm",
        "errorMessage": "Please fill out the required fields",
        missingFields
      });
    } else if (!this.phoneValidated()) {
      handleDisplayErrors({
        "component": "contactInformationForm",
        "errorMessage": "Please use a valid phone number",
        "missingFields": ["phoneNumber"]
      });
    }
  }

  /* eslint-disable react/destructuring-assignment */
  emptyInputsCheck = () => {
    const missingFieldsKeys = [];
    const validations = [
      "firstName",
      "lastName",
      "streetAddress",
      "city",
      "state",
      "zipCode",
      "emailAddress",
      "phoneNumber"
    ];

    validations.forEach((validation) => {
      if (this.state[validation] === "") {
        missingFieldsKeys.push(validation);
      }
    });

    if (missingFieldsKeys.length > 0) {
      return missingFieldsKeys;
    }
    return false;
  }

  updateValidatedEmail = (email) => {
    this.setState({"emailAddress": email});
  }

  emailValidated = () => {
    const {emailAddress} = this.state;
    return emailAddress.length > 0;
  }

  phoneValidated = () => {
    const {phoneNumber} = this.state;
    const validRegex = /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
    return phoneNumber.match(validRegex);
  }

  handleChange = (event) => {
    this.setState({[event.target.name]: event.target.value});
  }

  handleFailedAddress = () => {
    const {handleDisplayErrors} = this.props;
    this.clearContact();
    handleDisplayErrors({
      "component": "contactInformationForm",
      "errorMessage": "We're unable to verify that address. Please check your shipping info and try again.",
      "missingFields": [
        "streetAddress",
        "city",
        "state",
        "zipCode"
      ]
    });
  }

  setFailureMessage = () => {
    this.setState({"loading": false});
    this.handleFailedAddress();
  }

  setSuccessMessage = (response) => {
    this.setState({"loading": false});

    if (response.success) {
      this.mapVerifiedAddressToState(response.verified_address);
    } else {
      this.handleFailedAddress();
    }
  }

  mapVerifiedAddressToState = (verifiedAddress) => {
    const {city, name, phone, state, street1, zip} = verifiedAddress;
    const names = name.split(" ");

    this.setState({
      city,
      "firstName": names[0],
      "lastName": names[1],
      "phoneNumber": phone,
      state,
      "streetAddress": street1,
      "unitNumber": "",
      "zipCode": zip
    }, () => this.createContact());
  }

  verificationParamBuilder = () => {
    const {city, firstName, lastName, phoneNumber, state, streetAddress, unitNumber, zipCode} = this.state;

    return {
      city,
      "name": `${firstName} ${lastName}`,
      "phone": phoneNumber,
      state,
      "street1": streetAddress,
      "street2": unitNumber,
      "zip": zipCode
    };
  }

  verifyAddressAndCreateContact = () => {
    this.setState({"loading": true});
    if (this.addressInputsValidated() && this.emailValidated() && this.phoneValidated()) {

      const addressVerificationParams = this.verificationParamBuilder();

      BuyItNowAPICLIENT.verifyAddress(
        addressVerificationParams, this.setSuccessMessage, this.setFailureMessage
      );
    } else {
      this.clearContact();
      this.handleInputValidations();
      this.setState({"loading": false});
    }
  }

  addressInputsValidated = () => {
    const {
      city,
      firstName,
      lastName,
      phoneNumber,
      state,
      streetAddress,
      zipCode
    } = this.state;

    return city && firstName && lastName && phoneNumber && state && streetAddress && zipCode;
  };

  render () {
    const {loading} = this.state;

    const {error, displayed} = this.props;

    return (
      <div
        className={`${displayed ? "" : "hidden"} "mt16"`}
        id="contact-information-form"
      >
        {error}

        <ContactFormFields
          handleChange={this.handleChange}
          parentState={this.state}
          updateValidatedEmail={this.updateValidatedEmail}
        />
        <div>
          <button
            className="btn btn-primary centered save-and-continue"
            disabled={loading}
            onClick={this.verifyAddressAndCreateContact}
            type="submit"
          >
            {loading ? <LoadingSpinner /> : "Save and Continue"}
          </button>
        </div>
      </div>
    );
  }
}

ContactInformationForm.propTypes = {
  "clearContact": PropTypes.func.isRequired,
  "displayed": PropTypes.bool.isRequired,
  "error": PropTypes.object,
  "handleDisplayErrors": PropTypes.func.isRequired,
  "handleSuccessfulContactCreation": PropTypes.func.isRequired
};

ContactInformationForm.defaultProps = {"error": null};
